function _checkPrivateRedeclaration(obj, privateCollection) {
    if (privateCollection.has(obj)) {
        throw new TypeError("Cannot initialize the same private elements twice on an object");
    }
}
function _classApplyDescriptorGet(receiver, descriptor) {
    if (descriptor.get) {
        return descriptor.get.call(receiver);
    }
    return descriptor.value;
}
function _classApplyDescriptorSet(receiver, descriptor, value) {
    if (descriptor.set) {
        descriptor.set.call(receiver, value);
    } else {
        if (!descriptor.writable) {
            throw new TypeError("attempted to set read only private field");
        }
        descriptor.value = value;
    }
}
function _classExtractFieldDescriptor(receiver, privateMap, action) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to " + action + " private field on non-instance");
    }
    return privateMap.get(receiver);
}
function _classPrivateFieldGet(receiver, privateMap) {
    var descriptor = _classExtractFieldDescriptor(receiver, privateMap, "get");
    return _classApplyDescriptorGet(receiver, descriptor);
}
function _classPrivateFieldInit(obj, privateMap, value) {
    _checkPrivateRedeclaration(obj, privateMap);
    privateMap.set(obj, value);
}
function _classPrivateFieldSet(receiver, privateMap, value) {
    var descriptor = _classExtractFieldDescriptor(receiver, privateMap, "set");
    _classApplyDescriptorSet(receiver, descriptor, value);
    return value;
}
import { CeramicClient } from '@ceramicnetwork/http-client';
import { execute, parse, validate } from 'graphql';
import { Context } from './context.js';
import { createGraphQLSchema } from './schema.js';
var _context = /*#__PURE__*/ new WeakMap(), _resources = /*#__PURE__*/ new WeakMap(), _schema = /*#__PURE__*/ new WeakMap();
/**
 * The ComposeClient class provides APIs to execute queries on a GraphQL schema generated from a
 * `RuntimeCompositeDefinition`. It allows applications to interact with documents using known
 * models on a Ceramic node.
 *
 * It is exported by the {@linkcode client} module.
 *
 * ```sh
 * import { ComposeClient } from '@composedb/client'
 * ```
 */ export class ComposeClient {
    /**
   * Context instance used internally.
   */ get context() {
        return _classPrivateFieldGet(this, _context);
    }
    /**
   * DID instance used internally by the Ceramic client instance.
   */ get did() {
        return _classPrivateFieldGet(this, _context).ceramic.did;
    }
    /**
   * ID of the DID attached to the Ceramic client instance used internally. If `null`, the
   * Ceramic instance is not authenticated and mutations will fail.
   */ get id() {
        return _classPrivateFieldGet(this, _context).viewerID;
    }
    /**
   * CACAO resources URLs for the models the client interacts with.
   */ get resources() {
        return _classPrivateFieldGet(this, _resources);
    }
    /**
   * Attach the given DID instance to the Ceramic client instance used internally. An authenticated
   * DID instance is necessary to perform GraphQL mutations.
   */ setDID(did) {
        _classPrivateFieldGet(this, _context).ceramic.did = did;
    }
    /**
   * Execute a GraphQL query from a DocumentNode and optional variables.
   */ async execute(document, variableValues) {
        const errors = validate(_classPrivateFieldGet(this, _schema), document);
        return errors.length > 0 ? {
            errors
        } : await execute({
            document,
            variableValues,
            contextValue: _classPrivateFieldGet(this, _context),
            schema: _classPrivateFieldGet(this, _schema)
        });
    }
    /**
   * Execute a GraphQL query from its source and optional variables.
   */ async executeQuery(source, variableValues) {
        let document;
        try {
            document = parse(source);
        } catch (syntaxError) {
            return {
                errors: [
                    syntaxError
                ]
            };
        }
        return await this.execute(document, variableValues);
    }
    constructor(params){
        _classPrivateFieldInit(this, _context, {
            writable: true,
            value: void 0
        });
        _classPrivateFieldInit(this, _resources, {
            writable: true,
            value: void 0
        });
        _classPrivateFieldInit(this, _schema, {
            writable: true,
            value: void 0
        });
        const { ceramic , definition , ...contextParams } = params;
        const ceramiClient = typeof ceramic === 'string' ? new CeramicClient(ceramic) : ceramic;
        _classPrivateFieldSet(this, _context, new Context({
            ...contextParams,
            ceramic: ceramiClient
        }));
        _classPrivateFieldSet(this, _resources, Object.values(definition.models).map((model)=>{
            return `ceramic://*?model=${model.id}`;
        }));
        _classPrivateFieldSet(this, _schema, createGraphQLSchema({
            definition
        }));
    }
}
